import { useDidMount, useShowHide, useWillUnmount } from '@bcp-channels-sharedcomponents-reactjs/core/dist/$hooks';
import { useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { modals } from '@modals';
import {
	useFetchClickToCallAgentOpen,
	useFetchClickToCallIsOpen,
	useFetchClickToCallStatus,
	useFetchRetrieveCountries,
} from '@services';
import { AgentStatus, ClickToCallAgentOpenInputDto, WorkStatus } from '@api/izibizi-xp';
import { useNavigation } from '@navigation';
import { useLogger } from '@hooks';
import { useServiceResponse } from '@store';

interface RequestContactOutput {
	resources: RequestContactResources;
	phone: string;
	indicative: string;
	isAgentOpen: ClickToCallAgent;
	showDisclaimer: boolean;
	newRequestContact: () => void;
	validateClickToCallAgentOpen: () => void;
	setPhone: React.Dispatch<React.SetStateAction<string>>;
	setIndicative: React.Dispatch<React.SetStateAction<string>>;
	setContactToCallCountry: (value: string) => void;
	setShowDisclaimer: (value: boolean) => void;
}

interface RequestContactResources {
	disclaimer: string;
}

type ClickToCallAgent = 'Open' | 'Closed' | 'ErrorToCall' | 'None';
let intervalId: NodeJS.Timeout | undefined = undefined;
export const useRequestContactHelper = (): RequestContactOutput => {
	const logger = useLogger();
	const t = useTranslation();
	const { show, hide } = useShowHide(modals.waitingForCall);
	const { gotoHome } = useNavigation();

	const [isAgentOpen, setIsAgentOpen] = useState<ClickToCallAgent>('None');
	const [showDisclaimer, setShowDisclaimer] = useState<boolean>(false);

	const [indicative, setIndicative] = useState<string>('');
	const [phone, setPhone] = useState<string>('');
	const [contactToCallCountry, setContactToCallCountry] = useState<string>('');

	const { fetch: fetchClickToCallStatus, data: clickToCallStatusData } = useFetchClickToCallStatus();
	const { fetch: fetchClickToCallAgentOpen, data: clickToCallAgentOpenData } = useFetchClickToCallAgentOpen();
	const { fetch: fetchRetrieveCountries, data: retrieveCountriesData } = useFetchRetrieveCountries();
	const { fetch: fetchClickToCallIsOpen } = useFetchClickToCallIsOpen();
	const { clickToCallIsOpen } = useServiceResponse();

	const timeForInterval = useMemo(() => {
		const time = parseInt(t('placeholders.clickToCall.time'));
		logger('timeForInterval >', {
			timeOnSP: time,
			timeInMS: `${time} * 1000 = ${time * 1000}`,
			defaultTime: 10000,
		});
		return !isNaN(time) ? time * 1000 : 10000;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [t]);

	useDidMount(() => {
		fetchClickToCallIsOpen({});
		fetchRetrieveCountries({ useCache: false });
	});

	useWillUnmount(() => {
		clearCheckInterval();
	});

	useEffect(() => {
		logger('useEffect > clickToCallAgentOpenData', {
			clickToCallAgentOpenData,
		});
		if (intervalId === undefined && !CoreHelper.isNullOrEmpty(clickToCallAgentOpenData?.workItemId)) {
			setIntervalCall();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickToCallAgentOpenData?.workItemId]);

	useEffect(() => {
		if (
			!CoreHelper.isNullOrEmpty(clickToCallStatusData?.workStatus) &&
			clickToCallStatusData?.workStatus !== WorkStatus.PROCESSING
		) {
			clearCheckInterval();
			if (clickToCallStatusData?.workStatus === WorkStatus.ANSWER) {
				gotoHome();
			} else {
				setIsAgentOpen('ErrorToCall');
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickToCallStatusData?.workStatus]);

	useEffect(() => {
		if (
			!CoreHelper.isNullOrEmpty(clickToCallIsOpen?.agentStatus) &&
			!CoreHelper.isNullOrEmpty(retrieveCountriesData?.countries)
		) {
			logger('useEffect > clickToCallIsOpenData', {
				clickToCallIsOpen,
			});
			setIsAgentOpen(clickToCallIsOpen?.agentStatus === AgentStatus.OPEN ? 'Open' : 'Closed');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [clickToCallIsOpen?.agentStatus, retrieveCountriesData?.countries]);

	const resources = useMemo((): RequestContactResources => {
		return {
			disclaimer: t('placeholders.requestToCall.disclaimer'),
		};
	}, [t]);

	const validateClickToCallAgentOpen = () => {
		show();
		const data: ClickToCallAgentOpenInputDto = {
			phoneNumber: indicative + phone,
			phoneCountryCode: contactToCallCountry,
		};
		fetchClickToCallAgentOpen(data);
	};

	const clearCheckInterval = () => {
		if (intervalId !== undefined) {
			clearInterval(intervalId);
			hide();
			intervalId = undefined;
		}
	};

	const setIntervalCall = () => {
		intervalId = setInterval(() => {
			fetchClickToCallStatus({
				workItemId: clickToCallAgentOpenData?.workItemId,
			});
		}, timeForInterval);
	};

	const newRequestContact = useCallback(() => {
		setIsAgentOpen('Closed');
		setShowDisclaimer((oldValue) => !oldValue);
	}, []);

	return {
		resources,
		phone,
		indicative,
		isAgentOpen,
		showDisclaimer,
		validateClickToCallAgentOpen,
		setPhone,
		setIndicative,
		newRequestContact,
		setContactToCallCountry,
		setShowDisclaimer,
	};
};
